import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { useAuth } from "@/lib/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Swords, AlertCircle, Eye, EyeOff, ShieldOff } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  // account table: login accepts username OR email
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword]     = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [suspended, setSuspended] = useState(false);

  if (isAuthenticated) {
    navigate(createPageUrl("Dashboard"));
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuspended(false);
    setLoading(true);
    try {
      // server accepts { email } field — which can be email or username
      const user = await login(identifier, password);
      // Shouldn't reach here if suspended, but guard anyway
      if (user?.suspended) {
        setSuspended(true);
        return;
      }
      navigate(createPageUrl("Dashboard"));
    } catch (err) {
      if (err.message?.toLowerCase().includes('suspend')) {
        setSuspended(true);
      } else {
        setError(err.message || 'Login failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative">
      {/* Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#1a0a1a]/95 to-[#0a0a0a]" />
        <div className="absolute inset-0 bg-[url('https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/6915ad56fe64ad9662a36d0b/5e14522dd_loading_screen_01.png')] bg-cover bg-center opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/80 via-transparent to-[#0a0a0a]/90" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#0a0a0a_100%)]" />
      </div>

      <div className="relative z-10 py-24 flex items-center justify-center min-h-screen">
        <div className="max-w-md mx-auto px-4 w-full">

          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex items-center gap-3 justify-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-[#8B0000] to-[#B22222] rounded-lg flex items-center justify-center">
                <Swords className="w-7 h-7 text-white" />
              </div>
              <span className="text-2xl font-bold text-white">Knight Arena</span>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Welcome Back</h1>
            <p className="text-gray-400">Sign in to access your account</p>
          </div>

          <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-8">

            {/* Suspended account state */}
            {suspended ? (
              <div className="text-center py-8">
                <ShieldOff className="w-14 h-14 text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Account Suspended</h3>
                <p className="text-gray-400 text-sm mb-6">
                  This account has been suspended. Please contact{' '}
                  <Link to={createPageUrl("Support")} className="text-white hover:underline">support</Link>{' '}
                  if you believe this is an error.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setSuspended(false)}
                  className="border-[#8B0000] text-gray-300 hover:text-white hover:border-white"
                >
                  Back to Login
                </Button>
              </div>
            ) : (
              <>
                {error && (
                  <Alert variant="destructive" className="mb-6 bg-red-900/20 border-red-500/50">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">

                  {/* username or email — matches account.username / account.email */}
                  <div className="space-y-2">
                    <Label htmlFor="identifier" className="text-gray-300">Username or Email</Label>
                    <Input
                      id="identifier"
                      type="text"
                      autoComplete="username"
                      placeholder="DarkKnight99 or warlord@example.com"
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      required
                      className="bg-white/5 border-[#8B0000]/40 text-white placeholder:text-gray-600 focus:border-white/50"
                    />
                  </div>

                  {/* password */}
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-gray-300">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="bg-white/5 border-[#8B0000]/40 text-white placeholder:text-gray-600 focus:border-white/50 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-[#8B0000] to-[#B22222] hover:from-[#B22222] hover:to-[#8B0000] text-white hover:text-white py-6 text-lg border-none"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                        Signing in...
                      </span>
                    ) : 'Sign In'}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-gray-400 text-sm">
                    No account?{' '}
                    <Link to={createPageUrl("Register")} className="text-white hover:underline">
                      Join the Alpha
                    </Link>
                  </p>
                </div>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
